import type { MutableUnweightedGraph } from 'graphs-for-js';
import { Node as ReactFlowNode, Edge as ReactFlowEdge } from 'reactflow';
import { UrlFilters } from '../../hooks/useUrlFilters';
import type { Boundaries, Edge } from '../../services/gql/types';
import {
  ApiAssetType,
  AssetDisplayType,
  AssetLevel,
  LineageNode,
  ModelArtifactType,
  Report,
  RunStatus,
} from '../../types/types';

export enum PlatformType {
  Looker = 'looker',
  Tableau = 'tableau',
  PowerBI = 'powerbi',
  Sigma = 'sigma',
  ThoughtSpot = 'thoughtspot',
  Hex = 'hex',
  Lightdash = 'lightdash',
}

export enum OrchestratorType {
  Airflow = 'airflow',
  DbtCloud = 'dbt_cloud',
  GithubActions = 'github_actions',
  Dagster = 'dagster',
  Prefect = 'prefect',
}

export type ApiLineageNode = {
  id: string;
  type: ModelArtifactType;
  isRestricted?: boolean;
  displayType?: AssetDisplayType;
  sourceType?: string;
  artifactType?: ModelArtifactType;
  parent?: string;
  level?: AssetLevel;
  label?: string;
  tags?: string[];
  owners?: string[];
  databaseName?: string;
  schemaName?: string;
  tableName?: string;
  materialization?: string;
  path?: string;
  platform?: string;
  subType?: string;
  dataPlatform?: string;
  testExecutions?: {
    testName: string;
    columnName: string | null;
    executionStatus: RunStatus;
  }[];
  columnsCount?: number;
};

export type ApiLineage = {
  nodes: ApiLineageNode[];
  edges: [string, string][];
};

export type EnrichedLineageNode = LineageNode & {
  isRestricted?: boolean;
  label?: string;
  children?: EnrichedLineageNode[];
  tags?: string[];
  owners?: string[];
  job?: string;
  tests?: string[];
  databaseName?: string;
  schemaName?: string;
  tableName?: string;
  materialization?: string;
  path?: string;
  nodeType?: ModelArtifactType;
  artifactType?: ModelArtifactType;
  platform?: string;
  subType?: string;
  sourceType?: string;
  dataPlatform?: string;
  displayType?: string;
  testExecutions?: {
    testName: string;
    columnName: string | null;
    executionStatus: RunStatus;
  }[];
  columnsCount?: number;
};

export type EnrichedLineageData = {
  nodes: EnrichedLineageNode[];
  edges: [string, string][];
};

export type ReactFlowNodeData = {
  metadata: EnrichedLineageNode;
  children?: ReactFlowNode<ReactFlowNodeData>[];
  parentNode?: EnrichedLineageNode;
  totals?: Record<string, number>;
  height: number;
  width: number;
  isRootNode?: boolean;
  isFiltered?: boolean;
  isExpanded?: boolean;
  isOnHighlightPath?: boolean;
  columnsCount?: number;
  isColumnsLoading?: boolean;
  isColumnHighlightFiltered?: boolean;
  isLastCompactRow?: boolean;
  /** In pill form, the Y where an expanded node's last row ends. */
  compactPanelBottom?: number;
};

export type LogicalEdge = {
  source: string;
  target: string;
};

export type LineageReactFlowEdgeData = {
  logicalEdges?: LogicalEdge[];
};

export type LineageReactFlowEdge = ReactFlowEdge<LineageReactFlowEdgeData>;

export type ReactFlowGraph = {
  nodes: ReactFlowNode<ReactFlowNodeData>[];
  edges: LineageReactFlowEdge[];
};

export type HopLimit = { downstream?: number; upstream?: number };

export type StreamDirection = 'upstream' | 'downstream';

export type DagDepthBoundary = { incomingDepth: number; outgoingDepth: number };

export type GetGraphOnMessageData = {
  reportData?: Report;
  selectedNodes?: string[];
  filters?: UrlFilters;
  streamDirection?: StreamDirection;
  hopLimit?: HopLimit;
  reactFlowGraph?: ReactFlowGraph;
  highlightedNode?: string;
  expandNodes?: string[];
  collapseNodes?: string[];
  lockPosNodes?: string[];
  calcDownstreamUpstream?: boolean;
  excludeColumnNodes?: boolean;
  highlightColumnSubgraph?: ApiLineage;
  apiLineage?: ApiLineage;
  forceLoadLargeGraph?: boolean;
};

export type GetGraphPostMessageData = {
  rootNodes: EnrichedLineageNode[];
  nodes: EnrichedLineageNode[];
  edges: [string, string][];
  reactFlowGraph: ReactFlowGraph;
  upstreamNodes?: EnrichedLineageNode[];
  downstreamNodes?: EnrichedLineageNode[];
  dagDepthBoundary?: DagDepthBoundary;
  cycleNodes?: string[];
};

export type HighlightNodeOnMessageData = {
  nodeId?: string;
  reactFlowGraph: ReactFlowGraph;
};

export type HighlightNodePostMessageData = {
  reactFlowGraph: ReactFlowGraph;
};

export type ToggleExpandNodeOnMessageData = {
  node: ReactFlowNode<ReactFlowNodeData>;
  selectedNodes?: string[];
  highlightedNode?: string;
  reactFlowGraph: ReactFlowGraph;
};

export type ToggleExpandNodePostMessageData = {
  reactFlowGraph: ReactFlowGraph;
};

export type AddAssetColumnsOnMessageData = {
  assetId: string;
  columns: ApiLineageNode[];
  selectedNodes?: string[];
  highlightedNode?: string;
  reactFlowGraph: ReactFlowGraph;
  currentlyExpandedNodes?: string[];
};

export type AddAssetColumnsPostMessageData = {
  assetId: string;
  reactFlowGraph: ReactFlowGraph;
};

export type SetHighlightColumnSubgraphOnMessageData = {
  highlightColumnSubgraph?: ApiLineage;
  selectedNodes?: string[];
  highlightedNode?: string;
  reactFlowGraph: ReactFlowGraph;
  expandNodes?: string[];
  lockPosNodes?: string[];
};

export type SetHighlightColumnSubgraphPostMessageData = {
  reactFlowGraph: ReactFlowGraph;
};

export type OnMessageData =
  | {
      type: 'getGraph';
      data: GetGraphOnMessageData;
    }
  | {
      type: 'highlightNode';
      data: HighlightNodeOnMessageData;
    }
  | {
      type: 'toggleExpandNode';
      data: ToggleExpandNodeOnMessageData;
    }
  | {
      type: 'addAssetColumns';
      data: AddAssetColumnsOnMessageData;
    }
  | {
      type: 'setHighlightColumnSubgraph';
      data: SetHighlightColumnSubgraphOnMessageData;
    };

export type PostMessageData =
  | {
      type: 'getGraph';
      data: GetGraphPostMessageData;
    }
  | {
      type: 'highlightNode';
      data: HighlightNodePostMessageData;
    }
  | {
      type: 'toggleExpandNode';
      data: ToggleExpandNodePostMessageData;
    }
  | {
      type: 'addAssetColumns';
      data: AddAssetColumnsPostMessageData;
    }
  | {
      type: 'setHighlightColumnSubgraph';
      data: SetHighlightColumnSubgraphPostMessageData;
    }
  | {
      type: 'error';
      data?: { message: string; stack?: string };
    }
  | {
      type: 'largeGraphWarning';
      data?: null;
    };

export type WorkerData = {
  graph?: MutableUnweightedGraph<EnrichedLineageNode, string>;
  filteredGraph?: MutableUnweightedGraph<EnrichedLineageNode, string>;
  enrichedLineageData?: EnrichedLineageData;
  highlightColumnSubgraph?: ApiLineage;
  lazyLoadedColumns?: Record<string, ApiLineageNode[]>;
  filters?: UrlFilters;
  reportData?: Report;
};

export type InvocationData = {
  invocation_id?: string;
  job_url?: string;
  job_run_url?: string;
  job_name?: string;
  job_id?: string;
  job_run_id?: string;
  orchestrator?: string;
};

export type Invocation = {
  jobUrl?: string;
  jobRunUrl?: string;
  jobName?: string;
  jobId?: string;
  jobRunId?: string;
  orchestrator?: string;
};

export enum ResourceType {
  Model = 'model',
  Source = 'source',
  Exposure = 'exposure',
}

type BaseResource = {
  resourceType: ResourceType;
  name?: string;
  owners: Array<string>;
  tags: Array<string>;
  description?: string;
  meta?: { [key: string]: string };
  fqn: string;
};

type Model = BaseResource & {
  databaseName?: string;
  schemaName: string;
  tableName: string;
};

type Source = BaseResource & {
  databaseName?: string;
  schemaName: string;
  tableName: string;
};

export type Exposure = BaseResource & {
  label?: string;
  url?: string;
  type?: string;
  maturity?: string;
};

export type Resource = Model | Source | Exposure;

type ColumnLineageNode = {
  __typename?: string;
  id: string;
  parentId?: string;
  level?: AssetLevel;
  type: ApiAssetType;
  name: string;
  displayName?: string;
  displayType: AssetDisplayType;
  artifactType: ModelArtifactType;
  tags: string[];
  owners: string[];
  path?: string;
};

export type LineageLatestTestExecution = {
  test: {
    name: string;
  };
  execution: {
    status: RunStatus;
    columnName: string | null;
  };
};

export type LineageQueryResponse = {
  lineage: {
    assets: {
      edges: Array<Edge>;
      nodes: Array<{
        __typename?: string;
        id: string;
        parentId?: string;
        level?: AssetLevel;
        type: ApiAssetType;
        name: string;
        displayType: AssetDisplayType;
        artifactType: ModelArtifactType;
        tags: string[];
        owners: string[];
        dbName?: string;
        schemaName?: string;
        tableName?: string;
        materialization?: string;
        path?: string;
        platform?: string;
        biType?: string;
        semanticType?: string;
        dataPlatform?: string;
        sourceType?: string;
        columnsCount: number;
        latestTestExecutions?: LineageLatestTestExecution[];
      }>;
      boundaries: Boundaries;
    };
    columns?: {
      nodes: Array<ColumnLineageNode>;
      edges: Array<Edge>;
      boundaries: Boundaries;
      exceededLimitParentAssets: string[];
    };
  };
};

export type HighlightedGraphNodeState = {
  id: string;
  parentId?: string;
  metadata?: EnrichedLineageNode;
};

export type LineageInfoPanelState = {
  isOpen: boolean;
  tab?: 'testResults' | 'nodeInfo';
};

export type LineageZoomTier = 'card' | 'pill' | 'dot';

export type CompactLineageZoomTier = Exclude<LineageZoomTier, 'card'>;

export type LineageNodeDegrees = {
  byNodeId: Record<string, number>;
  max: number;
};

export type LineageNodeColors = {
  border: string;
  background: string;
};

export type GraphNodeBorderColorParams = {
  isHighlighted?: boolean;
  isRestricted?: boolean;
  colorByBorderColor: string;
};

export type {
  LineageColorBy,
  LineageDisplaySettings,
  LineageNodeDetailField,
  LineageNodeMetadataField,
} from '../../types/lineageDisplaySettingsTypes';

export type LineageColorCategory =
  | 'source'
  | 'dataAsset'
  | 'exposure'
  | 'semantic'
  | 'failing'
  | 'warning'
  | 'passing'
  | 'neutral';
